import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { get, HTTPError, post } from "../helpers/HTTP";

import type { PaymentSource } from "../types";

async function getPaymentSourcesRequest() {
  return (await get("billing/payment-sources")).payment_sources;
}
async function addPaymentSourceRequest(
  source: "card" | "paypal" | "apple-pay",
  token: string,
): Promise<PaymentSource> {
  return (await post(`billing/${source}`, { token })).payment_source;
}
export default function usePaymentSources() {
  const queryClient = useQueryClient();

  const queryKey = ["paymentSources"];

  const { data: paymentSources, isLoading: isLoadingPaymentSources } = useQuery<
    PaymentSource[]
  >({
    queryKey,
    queryFn: () => getPaymentSourcesRequest(),
  });

  const {
    mutateAsync: addPaymentSource,
    isPending: isAddingPaymentSource,
    error: addPaymentSourceError,
    reset: resetAddPaymentSourceError,
  } = useMutation({
    mutationFn: ({
      token,
      source,
    }: {
      token: string;
      source: "card" | "paypal" | "apple-pay";
    }) => addPaymentSourceRequest(source, token),
    onSuccess: (paymentSource) => {
      queryClient.setQueryData(
        queryKey,
        (prevPaymentSources: PaymentSource[]) => [
          paymentSource,
          ...prevPaymentSources,
        ],
      );

      toast.success("Payment method added");
    },
    onError: (error) =>
      toast.error(
        error instanceof HTTPError && error.serverMessage
          ? error.serverMessage
          : "Failed to add payment method",
      ),
  });
  return {
    paymentSources,
    isLoadingPaymentSources,
    addPaymentSource,
    isAddingPaymentSource,
    addPaymentSourceError,
    resetAddPaymentSourceError,
  };
}
